import { supabase } from '@/lib/supabase';

// Note: Some joined queries reference tables not yet in generated Database types.
// We cast the client to bypass strict type checking.
const db = supabase as any;

// =====================================================
// TYPES
// =====================================================

export interface CompanyFilters {
  search?: string;
  status?: 'active' | 'trial' | 'expired' | 'suspended' | 'all';
  plan?: string;
}

export interface UserFilters {
  search?: string;
  role?: 'owner' | 'admin' | 'user' | 'cashier' | 'all';
  companyId?: string;
  isActive?: boolean;
}

export interface CompanyWithDetails {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  created_at: string;
  users_count: number;
  license: {
    id: string;
    status: string;
    expires_at: string;
    plan_name: string;
    tier_level: number;
  } | null;
  monthly_revenue: number;
}

export interface UserWithCompany {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  is_super_admin: boolean;
  company_id: string | null;
  created_at: string;
  updated_at: string;
  company: { id: string; name: string } | null;
}

export interface CompanyStats {
  total: number;
  active: number;
  trial: number;
  expired: number;
  suspended: number;
}

export interface UserStats {
  total: number;
  active: number;
  inactive: number;
  admins: number;
}

// =====================================================
// SERVICE
// =====================================================

export const AdminCompanyService = {
  /**
   * Get all companies with license info and user counts
   */
  async getAllCompanies(filters?: CompanyFilters): Promise<CompanyWithDetails[]> {
    // Fetch companies with license/plan join
    let query = db
      .from('companies')
      .select(`
        id, name, email, phone, address, created_at,
        company_licenses(id, status, expires_at, plan:license_plans(name, tier_level))
      `)
      .order('created_at', { ascending: false });

    // Apply search filter
    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }

    const { data: companies, error } = await query;
    if (error) throw new Error(`Failed to fetch companies: ${error.message}`);

    // Get user counts per company in a single query
    const companyIds = (companies || []).map((c: any) => c.id);
    const { data: userCounts, error: ucError } = await db
      .from('users')
      .select('company_id')
      .in('company_id', companyIds);

    const countsMap: Record<string, number> = {};
    if (!ucError && userCounts) {
      for (const u of userCounts) {
        if (u.company_id) {
          countsMap[u.company_id] = (countsMap[u.company_id] || 0) + 1;
        }
      }
    }

    // Transform and filter
    let result: CompanyWithDetails[] = (companies || []).map((c: any) => {
      const license = c.company_licenses?.[0] || null;
      const planData = license?.plan;

      let status = 'expired';
      if (license) {
        if (license.status === 'suspended') status = 'suspended';
        else if (license.status === 'trial') status = 'trial';
        else if (license.status === 'active' && license.expires_at && new Date(license.expires_at) > new Date()) status = 'active';
        else if (license.status === 'active') status = 'expired';
        else status = license.status;
      }

      return {
        id: c.id,
        name: c.name,
        email: c.email,
        phone: c.phone,
        address: c.address,
        created_at: c.created_at,
        users_count: countsMap[c.id] || 0,
        license: license
          ? {
              id: license.id,
              status,
              expires_at: license.expires_at,
              plan_name: planData?.name || 'Unknown',
              tier_level: planData?.tier_level || 1,
            }
          : null,
        monthly_revenue: 0,
      };
    });

    // Apply status filter
    if (filters?.status && filters.status !== 'all') {
      result = result.filter((c) => {
        const companyStatus = c.license?.status || 'expired';
        return companyStatus === filters.status;
      });
    }

    // Apply plan filter
    if (filters?.plan && filters.plan !== 'all') {
      result = result.filter((c) => c.license?.plan_name === filters.plan);
    }

    return result;
  },

  /**
   * Get company stats by status
   */
  async getCompanyStats(): Promise<CompanyStats> {
    const { data: companies, error } = await db
      .from('companies')
      .select('id, company_licenses(status, expires_at)');

    if (error) throw new Error(`Failed to fetch company stats: ${error.message}`);

    let active = 0, trial = 0, expired = 0, suspended = 0;

    for (const c of companies || []) {
      const license = (c as any).company_licenses?.[0];
      if (!license) {
        expired++;
        continue;
      }
      if (license.status === 'suspended') suspended++;
      else if (license.status === 'trial') trial++;
      else if (license.status === 'active' && license.expires_at && new Date(license.expires_at) > new Date()) active++;
      else expired++;
    }

    return {
      total: (companies || []).length,
      active,
      trial,
      expired,
      suspended,
    };
  },

  /**
   * Get single company with full details
   */
  async getCompanyById(id: string): Promise<CompanyWithDetails | null> {
    const { data, error } = await db
      .from('companies')
      .select(`
        id, name, email, phone, address, created_at,
        company_licenses(id, status, expires_at, plan:license_plans(name, tier_level))
      `)
      .eq('id', id)
      .single();

    if (error) return null;

    const license = (data as any).company_licenses?.[0] || null;
    const planData = license?.plan;

    // Get user count
    const { count } = await db
      .from('users')
      .select('id', { count: 'exact', head: true })
      .eq('company_id', id);

    let status = 'expired';
    if (license) {
      if (license.status === 'suspended') status = 'suspended';
      else if (license.status === 'trial') status = 'trial';
      else if (license.status === 'active' && license.expires_at && new Date(license.expires_at) > new Date()) status = 'active';
      else status = license.status;
    }

    return {
      id: data.id,
      name: data.name,
      email: data.email,
      phone: data.phone,
      address: data.address,
      created_at: data.created_at,
      users_count: count || 0,
      license: license
        ? {
            id: license.id,
            status,
            expires_at: license.expires_at,
            plan_name: planData?.name || 'Unknown',
            tier_level: planData?.tier_level || 1,
          }
        : null,
      monthly_revenue: 0,
    };
  },

  /**
   * Get all users across all companies with filters
   */
  async getAllUsers(filters?: UserFilters): Promise<UserWithCompany[]> {
    let query = db
      .from('users')
      .select('*, company:companies(id, name)')
      .order('created_at', { ascending: false });

    if (filters?.search) {
      query = query.or(`full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }

    if (filters?.role && filters.role !== 'all') {
      query = query.eq('role', filters.role);
    }

    if (filters?.companyId) {
      query = query.eq('company_id', filters.companyId);
    }

    if (filters?.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }

    const { data, error } = await query;
    if (error) throw new Error(`Failed to fetch users: ${error.message}`);

    return (data || []).map((u: any) => ({
      id: u.id,
      email: u.email,
      full_name: u.full_name,
      role: u.role,
      is_active: u.is_active,
      is_super_admin: u.is_super_admin || false,
      company_id: u.company_id,
      created_at: u.created_at,
      updated_at: u.updated_at,
      company: u.company || null,
    }));
  },

  /**
   * Get user stats
   */
  async getUserStats(): Promise<UserStats> {
    const { data, error } = await db
      .from('users')
      .select('is_active, role');

    if (error) throw new Error(`Failed to fetch user stats: ${error.message}`);

    const users = data || [];
    return {
      total: users.length,
      active: users.filter((u: any) => u.is_active).length,
      inactive: users.filter((u: any) => !u.is_active).length,
      admins: users.filter((u: any) => u.role === 'admin' || u.role === 'owner').length,
    };
  },

  /**
   * Update a user (role, active status)
   */
  async updateUser(id: string, updates: { role?: string; is_active?: boolean }): Promise<void> {
    const { error } = await db
      .from('users')
      .update(updates)
      .eq('id', id);

    if (error) throw new Error(`Failed to update user: ${error.message}`);
  },
};
